/**
 * This utility should be modeled after the well-established PHP utility of the
 * same name, Url::isVimeoRedirectableUrl.
 *
 * It is currently an extremely stripped down version of that PHP utility because
 * our immediate use case demands only that we enforce relative urls. However, I
 * can easily see the demand for a more thorough isVimeoRedirectUrl function in
 * the future, so consider this a placeholder for that functionality.
 */

/**
 * Returns whether a url string is absolute.
 */
function isUrlAbsolute(url: string): boolean {
  const regex = /^https?:\/\/|^\/\//i;
  return regex.test(url);
}

/**
 * Returns whether a url string is relative.
 */
function isUrlRelative(url: string): boolean {
  return !isUrlAbsolute(url) && url[0] === '/';
}

/**
 * Check if the url's host is part of the global allow list.
 */
function isUrlHostInAllowList(url: string): boolean {
  const allowListHosts = [
    'ci.vimeows.com',
    'help.vimeo.com',
    'vimeo.com',
    'vimeo.dev',
    'vimeo.zendesk.com',
    'vimeopro.com',
    'vimeotest.dev',
    // Venues domains
    'feature-slvhyk3sjq-ue.a.run.app',
    'spa-v2-sim-2-slvhyk3sjq-ue.a.run.app',
    'dev.venues.vimeows.com',
    'venues.vimeo.com',
    'localhost',
    // Player domains
    'player2.vimeo.dev', // dev
    'master.playerci.vimeows.com', // ci
    'playerci.vimeows.com', // ci (test branch)
    'player.vimeo.com', // prod
  ];

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch (e) {
    // Invalid url.
    return false;
  }

  return (
    allowListHosts.includes(parsedUrl.host) ||
    allowListHosts.includes(parsedUrl.hostname) ||
    allowListHosts.some(
      (host) => parsedUrl.host.endsWith(`.${host}`) || parsedUrl.hostname.endsWith(`.${host}`),
    )
  );
}

/**
 * Returns whether a url string is a valid Vimeo url. Currently returns true only
 * for relative urls.
 */
export function isVimeoRedirectableUrl(url: string, customAllowList: string[] = []): boolean {
  // If the url is found is the passed customAllowList, return true.
  if (customAllowList.length && customAllowList.includes(url)) {
    return true;
  }

  // If the url is relative, return true.
  if (url && isUrlRelative(url)) {
    return true;
  }

  // If the url's host is in our allow list, return true.
  return isUrlHostInAllowList(url);
}
